import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "sonner";
import "@/App.css";
import "@/styles/preview-palette.css";
import "@/styles/preview-v2.css";
import "@/styles/phase1.css";
import "@/styles/phase1-revised.css";
import PreviewV2Landing from "@/pages/PreviewV2Landing";

import Nav from "@/components/sections/Nav";
import Hero from "@/components/sections/Hero";
import Marquee from "@/components/sections/Marquee";
import Narrative from "@/components/sections/Narrative";
import AnxietyEMDR from "@/components/sections/AnxietyEMDR";
import About from "@/components/sections/About";
import Modalities from "@/components/sections/Modalities";
import Philosophy from "@/components/sections/Philosophy";
import Reflections from "@/components/sections/Reflections";
import WhatToExpect from "@/components/sections/WhatToExpect";
import FAQ from "@/components/sections/FAQ";
import ContactSection from "@/components/sections/ContactSection";
// OtherPracticeTeaser intentionally not imported — hidden from public homepage
// per iter_17 user request. The file remains in the codebase so it can be
// re-imported without any rebuild work when the esoteric side is reactivated.
import Footer from "@/components/sections/Footer";
import RevealOnScroll from "@/components/RevealOnScroll";
import SectionDivider from "@/components/SectionDivider";
import Seo from "@/components/Seo";
import EMDRPage from "@/pages/EMDRPage";
import EMDRIntensivePage from "@/pages/EMDRIntensivePage";
import BrainspottingPage from "@/pages/BrainspottingPage";
import ACTPage from "@/pages/ACTPage";
import DBTPage from "@/pages/DBTPage";
import ApproachPage from "@/pages/ApproachPage";
import AlchemyExtendedPage from "@/pages/AlchemyExtendedPage";
import InnerCompassPage from "@/pages/InnerCompassPage";
import AlchemistsTablePage from "@/pages/AlchemistsTablePage";
import AlchemistTableAdminPage from "@/pages/AlchemistTableAdminPage";
import BookAReadingPage from "@/pages/BookAReadingPage";
import AlchemyReadingsPage from "@/pages/AlchemyReadingsPage";
import JournalPullPage from "@/pages/JournalPullPage";
import AlchemyInsightsPage from "@/pages/AlchemyInsightsPage";
import BlogPostPage from "@/pages/BlogPostPage";
import AlchemyInsightsAdminPage from "@/pages/AlchemyInsightsAdminPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import ResourcesPage from "@/pages/ResourcesPage";
import AdminResourcesPage from "@/pages/AdminResourcesPage";
import ResourceDetailPage from "@/pages/ResourceDetailPage";
// AnnouncementModal (EMDR Intensives popup) intentionally not imported — hidden
// from the homepage per iter_20 user request. The component file remains in the
// codebase so it can be re-imported and rendered inside <Landing /> again later
// without any rebuild work.

const homepageJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://alchemypractice.com/#business",
    name: "Alchemy Practice",
    description:
        "Boutique virtual therapy practice specializing in EMDR, Brainspotting, ACT, and DBT for anxiety, trauma, and self-doubt in Wisconsin and Colorado.",
    url: "https://alchemypractice.com",
    image:
        "https://customer-assets.emergentagent.com/job_practice-portal-15/artifacts/w2lq6860_Logo%20Title%20-%20no%20background.png",
    additionalType: "https://schema.org/MedicalBusiness",
    founder: {
        "@type": "Person",
        name: "Austine, LPC",
        jobTitle: "Licensed Professional Counselor",
    },
    address: {
        "@type": "PostalAddress",
        addressLocality: "Fitchburg",
        addressRegion: "WI",
        addressCountry: "US",
    },
    areaServed: [
        { "@type": "State", name: "Wisconsin" },
        { "@type": "State", name: "Colorado" },
    ],
    medicalSpecialty: "Psychiatric",
    knowsAbout: [
        "Psychotherapy",
        "EMDR Therapy",
        "Brainspotting",
        "ACT Therapy",
        "DBT Therapy",
    ],
};

const Landing = () => (
    <main className="bg-ivory text-charcoal">
        <Seo
            title="Alchemy Practice | EMDR Therapy in Wisconsin & Colorado | Austine LPC"
            description="Virtual EMDR therapy for anxiety, trauma, and self-doubt in Wisconsin and Colorado. Boutique telehealth practice serving Madison, Fitchburg, Denver, and beyond. Austine LPC."
            jsonLd={homepageJsonLd}
        />
        {/* <AnnouncementModal /> — EMDR Intensives popup hidden in iter_20. The
            /emdr-intensive page and its nav link remain fully live. To restore
            the popup, re-add the import above and uncomment this line. */}
        <Nav />
        <RevealOnScroll>
            <Hero />
            <Marquee />
            <SectionDivider color="gold" bg="#FAFAF8" />
            <Narrative />
            <SectionDivider color="sage" bg="#FAFAF8" />
            <AnxietyEMDR />
            <SectionDivider color="rose" bg="#EEF2ED" />
            <About />
            <SectionDivider color="gold" bg="#F2EDE6" />
            <Modalities />
            <SectionDivider color="sage" bg="#FAFAF8" />
            <Philosophy />
            <SectionDivider color="rose" bg="#F2EDE6" />
            <WhatToExpect />
            <SectionDivider color="gold" bg="#EEF2ED" />
            <Reflections />
            <SectionDivider color="sage" bg="#EEF2ED" />
            <FAQ />
            <SectionDivider color="rose" bg="#FAFAF8" />
            <ContactSection />
            <SectionDivider color="sage" bg="#FAFAF8" />
            <Footer />
        </RevealOnScroll>
    </main>
);

/**
 * Palette-preview wrapper (iter_24).
 * Renders the exact same <Landing /> inside a themed container so that every
 * selector in preview-palette.css takes effect. Zero markup, copy, or layout
 * changes — only CSS color overrides scoped to [data-theme="preview"].
 * The live site at "/" is unaffected.
 */
const LandingPreview = () => (
    <div data-theme="preview" data-testid="palette-preview-root">
        <div className="preview-banner" data-testid="preview-banner">
            Palette Preview — <strong>Ivory · Midnight · Gold</strong> — not live
        </div>
        <Landing />
    </div>
);

const PHASE1_FONTS = "https://fonts.googleapis.com/css2?family=Bodoni+Moda:ital,opsz,wght@0,6..96,400;0,6..96,500;1,6..96,400;1,6..96,500&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&family=Manrope:wght@400;500;600&display=swap";

// Phase 1 brand refresh preview — same homepage, restyled via styles/phase1.css
// Live homepage uses it too (approved Phase 1, DM Sans); preview routes stay noindex.
const LandingPhase1 = ({ font, live = false }) => (
    <div data-theme="phase1" data-font={font} data-testid={`phase1-root-${font}`}>
        <Helmet>
            <link rel="stylesheet" href={PHASE1_FONTS} />
            {!live && <meta name="robots" content="noindex, nofollow" />}
        </Helmet>
        <Landing />
    </div>
);

// Phase 1 revised preview — original typography, no teal (awaiting approval)
const LandingPhase1Revised = () => (
    <div data-theme="phase1r" data-testid="phase1r-root">
        <Helmet>
            <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Landing />
    </div>
);

function App() {
    return (
        <HelmetProvider>
            <div className="App">
                <Toaster
                    position="top-center"
                    toastOptions={{
                        style: {
                            background: "#FAFAF8",
                            color: "#2C2825",
                            border: "1px solid #9B7D5A",
                            fontFamily: "'Figtree', sans-serif",
                            letterSpacing: "0.02em",
                        },
                    }}
                />
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/preview" element={<LandingPreview />} />
                        <Route path="/preview-v2" element={<PreviewV2Landing />} />
                        <Route path="/preview-phase1" element={<LandingPhase1Revised />} />
                        <Route path="/preview-phase1-manrope" element={<LandingPhase1 font="manrope" />} />
                        <Route path="/emdr" element={<EMDRPage />} />
                        <Route path="/emdr-intensive" element={<EMDRIntensivePage />} />
                        <Route path="/brainspotting" element={<BrainspottingPage />} />
                        <Route path="/act" element={<ACTPage />} />
                        <Route path="/dbt" element={<DBTPage />} />
                        <Route path="/approach" element={<ApproachPage />} />
                        <Route path="/alchemy-extended" element={<AlchemyExtendedPage />} />
                        <Route path="/alchemy-extended/alchemy-readings" element={<AlchemyReadingsPage />} />
                        <Route path="/alchemy-extended/journal-pull" element={<JournalPullPage />} />
                        <Route path="/inner-compass" element={<InnerCompassPage />} />
                        <Route path="/alchemists-table" element={<AlchemistsTablePage />} />
                        <Route path="/alchemist-table/admin" element={<AlchemistTableAdminPage />} />
                        <Route path="/other-services" element={<Navigate to="/reflection-deck" replace />} />
                        <Route path="/reflection-deck" element={<AlchemyReadingsPage />} />
                        <Route path="/book-a-reading" element={<BookAReadingPage />} />
                        <Route path="/alchemy-insights" element={<AlchemyInsightsPage />} />
                        <Route path="/alchemy-insights/admin" element={<AlchemyInsightsAdminPage />} />
                        <Route path="/alchemy-insights/:slug" element={<BlogPostPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/resources" element={<ResourcesPage />} />
                        <Route path="/resources/admin" element={<Navigate to="/admin/resources" replace />} />
                        <Route path="/admin/resources" element={<AdminResourcesPage />} />
                        <Route path="/resources/:slug" element={<ResourceDetailPage />} />
                        <Route path="/admin/resources/reset" element={<AdminResourcesPage />} />
                    </Routes>
                </BrowserRouter>
            </div>
        </HelmetProvider>
    );
}

export default App;
